import { 
  Mail, Share2, Brain, Users, Target, TrendingUp, Shield, Zap, 
  Globe, Settings, MessageCircle, Briefcase, Clock, FileText,
  BarChart3, BookOpen, Lightbulb, Network, Eye, ArrowRight
} from 'lucide-react';
import { ProductConfigs } from './types';

export const communicationConsultingSolutionsConfigs: ProductConfigs = {
  // Communication Products - Enhanced Features
  'email-marketing-automation': {
    keyBenefits: [
      {
        icon: Brain,
        title: 'AI-Powered Content Generation',
        description: 'Advanced AI creates personalized email content with dynamic subject lines and adaptive messaging',
        metrics: '350% open rate increase',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Users,
        title: 'Behavioral Segmentation Engine',
        description: 'Real-time customer behavior analysis for hyper-targeted email campaigns and personalization',
        metrics: '85% segmentation accuracy',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Predictive Send Time Optimization',
        description: 'ML-powered send time prediction for maximum engagement based on individual user patterns',
        metrics: '40% engagement boost',
        color: 'from-green-500 to-emerald-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Target,
        title: 'Journey Orchestration Intelligence',
        description: 'Complex multi-path email journeys with AI-driven branching logic and outcome prediction',
        metrics: 'Smart journey automation',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Shield,
        title: 'Deliverability Optimization Suite',
        description: 'Advanced IP warming, domain reputation management, and spam filter testing',
        metrics: '99.5% inbox delivery',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Globe,
        title: 'Multi-Channel Campaign Sync',
        description: 'Synchronized messaging across email, SMS, push notifications, and social channels',
        metrics: 'Omnichannel consistency',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Enterprise Email Security',
        description: 'End-to-end encryption with GDPR compliance and advanced data protection protocols',
        metrics: 'Bank-grade security',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Performance Analytics',
        description: 'Instant campaign insights with live heat maps and engagement tracking',
        metrics: 'Real-time intelligence',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Settings,
        title: 'Scalable Email Infrastructure',
        description: 'Cloud-native platform supporting millions of emails with auto-scaling architecture',
        metrics: 'Unlimited email capacity',
        color: 'from-emerald-500 to-green-600'
      }
    ]
  },

  'social-media-management': {
    keyBenefits: [
      {
        icon: Brain,
        title: 'AI Content Creation & Optimization',
        description: 'Intelligent content generation with platform-specific optimization and viral prediction',
        metrics: '500% content engagement',
        color: 'from-pink-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Social Listening Intelligence',
        description: 'Advanced sentiment analysis with trend prediction and crisis detection capabilities',
        metrics: '95% sentiment accuracy',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Users,
        title: 'Influencer Discovery Engine',
        description: 'AI-powered influencer matching with authenticity scoring and ROI prediction',
        metrics: '10x influencer ROI',
        color: 'from-green-500 to-teal-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Target,
        title: 'Cross-Platform Campaign Orchestration',
        description: 'Unified campaign management across all social platforms with adaptive content optimization',
        metrics: 'Platform-optimized reach',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Globe,
        title: 'Global Social Commerce Integration',
        description: 'Direct social selling capabilities with shoppable posts and conversion tracking',
        metrics: 'Social commerce ready',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: BarChart3,
        title: 'Competitive Social Intelligence',
        description: 'Comprehensive competitor analysis with content gap identification and strategy insights',
        metrics: 'Competitive advantage',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Social Media Security Suite',
        description: 'Account protection with secure scheduling and brand safety monitoring',
        metrics: 'Complete account security',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Social Monitoring',
        description: 'Instant mention tracking with automated response triggers and crisis alerts',
        metrics: '24/7 social surveillance',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Settings,
        title: 'Enterprise Social Platform',
        description: 'Scalable infrastructure supporting unlimited accounts and team collaboration',
        metrics: 'Unlimited social scale',
        color: 'from-emerald-500 to-green-600'
      }
    ]
  },

  // Consulting Products - Enhanced Features
  'management-consulting-platform': {
    keyBenefits: [
      {
        icon: Briefcase,
        title: 'AI-Powered Methodology Engine',
        description: 'Intelligent consulting framework selection with best practice recommendations and outcome prediction',
        metrics: '300% project success rate',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Users,
        title: 'Client Engagement Intelligence',
        description: 'Advanced stakeholder mapping with sentiment tracking and engagement optimization strategies',
        metrics: '95% client satisfaction',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: TrendingUp,
        title: 'Predictive Project Analytics',
        description: 'ML-powered project outcome prediction with risk assessment and success factor analysis',
        metrics: '85% project prediction accuracy',
        color: 'from-green-500 to-emerald-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: BookOpen,
        title: 'Knowledge Management AI',
        description: 'Intelligent knowledge extraction and application with context-aware recommendations',
        metrics: 'Institutional knowledge leverage',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Target,
        title: 'Deliverable Quality Engine',
        description: 'Automated quality assurance with best practice validation and improvement suggestions',
        metrics: 'Exceptional deliverable quality',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Globe,
        title: 'Multi-Client Portfolio Management',
        description: 'Centralized engagement management with resource optimization and capacity planning',
        metrics: 'Portfolio optimization',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Enterprise Consulting Security',
        description: 'Confidential client data protection with advanced encryption and access controls',
        metrics: 'Client confidentiality',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Collaboration Engine',
        description: 'Instant team coordination with live document collaboration and progress tracking',
        metrics: 'Seamless team coordination',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Settings,
        title: 'Scalable Consulting Infrastructure',
        description: 'Enterprise-grade platform supporting unlimited engagements and global teams',
        metrics: 'Global consulting scale',
        color: 'from-emerald-500 to-green-600'
      }
    ]
  },

  'business-advisory-strategy-platform': {
    keyBenefits: [
      {
        icon: Brain,
        title: 'AI Strategic Intelligence Engine',
        description: 'Advanced strategic analysis with market opportunity identification and competitive positioning',
        metrics: '400% strategic insight depth',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: TrendingUp,
        title: 'Predictive Market Analytics',
        description: 'Future market trend prediction with scenario modeling and impact assessment',
        metrics: '90% trend prediction accuracy',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Target,
        title: 'Strategic Planning Automation',
        description: 'Intelligent strategy formulation with goal alignment and success probability scoring',
        metrics: 'Optimized strategy outcomes',
        color: 'from-purple-500 to-indigo-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: BarChart3,
        title: 'Financial Modeling Excellence',
        description: 'Advanced financial modeling with sensitivity analysis and risk-adjusted projections',
        metrics: 'Precision financial models',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Globe,
        title: 'Competitive Intelligence Hub',
        description: 'Real-time competitor monitoring with strategic move prediction and response planning',
        metrics: 'Competitive advantage insights',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Lightbulb,
        title: 'Innovation Opportunity Engine',
        description: 'AI-powered innovation discovery with market gap analysis and technology trend mapping',
        metrics: 'Innovation opportunity identification',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Strategic Data Protection',
        description: 'Confidential strategy protection with encrypted data and secure collaboration environments',
        metrics: 'Strategic confidentiality',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Strategy Monitoring',
        description: 'Live strategy performance tracking with automated alerts and adjustment recommendations',
        metrics: 'Dynamic strategy management',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Settings,
        title: 'Enterprise Strategy Platform',
        description: 'Scalable strategic planning infrastructure supporting complex organisational hierarchies',
        metrics: 'Enterprise strategy scale',
        color: 'from-emerald-500 to-green-600'
      }
    ]
  },

  'professional-services-automation': {
    keyBenefits: [
      {
        icon: Clock,
        title: 'AI-Powered Time Intelligence',
        description: 'Intelligent time tracking with project estimation and productivity optimization insights',
        metrics: '350% time accuracy',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Users,
        title: 'Resource Optimization Engine',
        description: 'Smart consultant allocation with skill matching and utilization rate optimization',
        metrics: '95% resource efficiency',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Profitability Analytics Suite',
        description: 'Real-time project profitability analysis with margin optimization and cost prediction',
        metrics: '40% margin improvement',
        color: 'from-purple-500 to-pink-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Target,
        title: 'Predictive Project Management',
        description: 'AI-powered project outcome prediction with risk mitigation and success factor analysis',
        metrics: 'Project success optimization',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Performance Intelligence Dashboard',
        description: 'Comprehensive consultant performance analytics with development recommendations',
        metrics: 'Performance excellence',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Automated Billing Intelligence',
        description: 'Smart billing automation with compliance checking and revenue recognition optimization',
        metrics: 'Billing accuracy & speed',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Financial Data Security',
        description: 'Secure financial processing with encrypted timesheet data and audit trail protection',
        metrics: 'Financial data protection',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Zap,
        title: 'Real-Time PSA Intelligence',
        description: 'Instant project insights with live utilization tracking and profitability alerts',
        metrics: 'Real-time PSA visibility',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Settings,
        title: 'Scalable PSA Infrastructure',
        description: 'Enterprise PSA platform supporting unlimited consultants and complex billing structures',
        metrics: 'Unlimited PSA scale',
        color: 'from-emerald-500 to-green-600'
      }
    ]
  },

  'client-portal-collaboration': {
    keyBenefits: [
      {
        icon: Users,
        title: 'AI-Enhanced Client Experience',
        description: 'Intelligent client portal with personalized dashboards and proactive communication',
        metrics: '90% client satisfaction',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: FileText,
        title: 'Smart Document Intelligence',
        description: 'AI-powered document organisation with intelligent search and automated categorisation',
        metrics: '80% faster document access',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: MessageCircle,
        title: 'Collaborative Communication Hub',
        description: 'Unified communication platform with threaded discussions and smart notification management',
        metrics: '300% communication efficiency',
        color: 'from-green-500 to-teal-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Target,
        title: 'Project Transparency Engine',
        description: 'Real-time project visibility with milestone tracking and progress visualization',
        metrics: 'Complete project transparency',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Client Success Analytics',
        description: 'Advanced client engagement analytics with satisfaction prediction and improvement insights',
        metrics: 'Client success optimization',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Globe,
        title: 'Multi-Stakeholder Coordination',
        description: 'Complex stakeholder management with role-based access and workflow automation',
        metrics: 'Stakeholder alignment',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Enterprise Portal Security',
        description: 'Advanced security controls with SSO integration and granular permission management',
        metrics: 'Enterprise-grade security',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Collaboration Engine',
        description: 'Instant updates with live document editing and real-time notification delivery',
        metrics: 'Seamless real-time collaboration',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Settings,
        title: 'Scalable Portal Platform',
        description: 'White-label portal infrastructure supporting unlimited clients and custom branding',
        metrics: 'Unlimited client portals',
        color: 'from-emerald-500 to-green-600'
      }
    ]
  },

  'consulting-intelligence-ai': {
    keyBenefits: [
      {
        icon: Brain,
        title: 'AI Research Automation Engine',
        description: 'Autonomous market research with intelligent data synthesis and insight generation',
        metrics: '1000% research speed',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Predictive Analytics Intelligence',
        description: 'Advanced predictive modeling for client outcomes with confidence scoring and scenario analysis',
        metrics: '95% prediction accuracy',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Lightbulb,
        title: 'Intelligent Proposal Generation',
        description: 'AI-powered proposal creation with win probability optimization and content personalization',
        metrics: '300% proposal win rate',
        color: 'from-green-500 to-emerald-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: BookOpen,
        title: 'Knowledge Graph Intelligence',
        description: 'Advanced knowledge extraction with relationship mapping and contextual recommendations',
        metrics: 'Intelligent knowledge leverage',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Eye,
        title: 'Competitive Intelligence AI',
        description: 'Automated competitor analysis with strategic move prediction and market positioning insights',
        metrics: 'Strategic intelligence advantage',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Target,
        title: 'AI-Powered Recommendation Engine',
        description: 'Intelligent best practice recommendations with success pattern recognition',
        metrics: 'Optimized consulting outcomes',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'AI Security & Privacy',
        description: 'Secure AI processing with encrypted model training and confidential data protection',
        metrics: 'AI-grade security',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: Zap,
        title: 'Real-Time AI Intelligence',
        description: 'Instant AI-powered insights with live data processing and automated decision support',
        metrics: 'Real-time AI insights',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Settings,
        title: 'Scalable AI Infrastructure',
        description: 'Enterprise AI platform with unlimited model complexity and global deployment capability',
        metrics: 'Unlimited AI scale',
        color: 'from-emerald-500 to-green-600'
      }
    ]
  },

  'mena-regional-consulting': {
    keyBenefits: [
      {
        icon: Globe,
        title: 'Regional Market Intelligence',
        description: 'Comprehensive MENA market analysis with cultural insights and regulatory compliance guidance',
        metrics: 'Regional expertise advantage',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Users,
        title: 'Cultural Adaptation Engine',
        description: 'AI-powered cultural sensitivity analysis with localized business practice recommendations',
        metrics: '95% cultural alignment',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: TrendingUp,
        title: 'Government Relations Intelligence',
        description: 'Advanced government engagement tracking with tender opportunity identification and success optimization',
        metrics: '300% tender success rate',
        color: 'from-green-500 to-teal-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Network,
        title: 'Partnership Ecosystem Mapping',
        description: 'Intelligent partner identification with relationship strength analysis and collaboration optimization',
        metrics: 'Strategic partnership success',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: BarChart3,
        title: 'Multi-Jurisdiction Compliance',
        description: 'Automated compliance monitoring across MENA jurisdictions with regulatory change alerts',
        metrics: '100% compliance assurance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Target,
        title: 'Regional Economic Intelligence',
        description: 'Advanced economic trend analysis with market opportunity identification and risk assessment',
        metrics: 'Economic advantage insights',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Regional Data Sovereignty',
        description: 'Compliant data handling with regional data protection and government security requirements',
        metrics: 'Regional compliance security',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Regional Monitoring',
        description: 'Instant regional market updates with regulatory change alerts and opportunity notifications',
        metrics: '24/7 regional intelligence',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Settings,
        title: 'Scalable Regional Platform',
        description: 'Multi-country consulting infrastructure with localised deployment and cultural customisation',
        metrics: 'Regional scale optimization',
        color: 'from-emerald-500 to-green-600'
      }
    ]
  }
};